import CurrentUserContext from "@/Contexts/CurrentUser";
import { UserSettingsDocument, UpdateUserSettingDocument } from "@/graphql/generated/graphql";
import { displayNotification } from "@/utils/notifications";
import { useQuery, useMutation } from "@apollo/client/react";
import { Autocomplete, FormControlLabel, FormGroup, Grid, Switch, TextField, Typography } from "@mui/material"
import dayjs from "dayjs";
import { titleize } from "inflection";
import { map } from "lodash";
import { useContext } from "react";

const switchOptions = ["instant_email", "due_task_email"]; // snake-case to match how the keys are stored in the database
const dateFormatOptions = ["YYYY-MM-DD", "MM/DD/YYYY", "DD/MM/YYYY", "DD-MMM-YYYY", "MMMM DD, YYYY", "DD MMMM YYYY"];

const UserPreferences: React.FC = () => {
  const { dateTimePreference } = useContext(CurrentUserContext);
  const { data, loading } = useQuery(UserSettingsDocument);
  const taskPreferences = data?.userSettings ? JSON.parse(data.userSettings.taskPreferences || '{}') : {};

  const [update] = useMutation(UpdateUserSettingDocument, {
    onCompleted: () => {
      displayNotification({
        type: "success",
        text: "User preferences updated successfully",
      })
    },
    onError: () => {
      displayNotification({
        type: "error",
        text: "Unable to update user preferences, try again later.",
      })
    },
    refetchQueries: ['userSettings']
  });

  const handleOnChange = (key: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const updatedPreferences = {
      ...taskPreferences,
      [key]: e.target.checked,
    };

    update({
      variables: {
        name: 'task_preferences',
        value: JSON.stringify(updatedPreferences)
      },
    });
  }

  const handleChangeDateTimePreference = (key: string, value: string) => {
    const updatedValues = {
      ...dateTimePreference,
      [key]: value,
    };

    update({
      variables: {
        name: 'date_time_preference',
        value: JSON.stringify(updatedValues)
      },
    });
  }

  if (loading) return <></>;

  return (
    <Grid container direction="column">
      <Typography sx={{ mt: 2 }} variant="h6" gutterBottom>User Preferences</Typography>
      <Autocomplete
        options={dateFormatOptions}
        getOptionLabel={(option) => dayjs().format(option)}
        disableClearable
        fullWidth
        value={dateTimePreference.date}
        onChange={(_, newValue) => handleChangeDateTimePreference('date', newValue)}
        renderInput={(params) => (
          <TextField {...params} label="Date Format" variant="outlined" placeholder="Date Format" size="small" sx={{ minWidth: "100px" }} fullWidth />
        )}
      />

      <FormGroup>
        <FormControlLabel
          key={`switch-time-${dateTimePreference.time}`}
          control={
            <Switch
              checked={(dateTimePreference.time === "HH:mm") as boolean}
              onChange={(_, checked) => handleChangeDateTimePreference("time", (checked ? "HH:mm" : "h:mm A"))}
            />
          }
          label="Enable 24 Hours"
        />
      </FormGroup>

      <FormGroup>
        {
          map(switchOptions, (opt) => {
            const value = !!taskPreferences[opt];

            return (
              <FormControlLabel
                key={`switch-${opt}`}
                control={
                  <Switch
                    checked={value}
                    onChange={(e) => {
                      handleOnChange(opt, e)
                    }}
                  />
                }
                label={titleize(opt)}
              />
            )
          })
        }
      </FormGroup>
    </Grid >
  );
}

export default UserPreferences;
